import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../lib/auth/AuthProvider';
import { useStore, Environment, Variable } from '../stores';
import { createEnvironmentApi } from '../lib/api/environmentApi';
import { generateId } from '../lib/utils';

/**
 * Hook for managing environments via the backend API.
 *
 * - Fetches environments when authenticated + workspace selected
 * - Falls back to local-only store operations when unauthenticated
 * - Provides CRUD for environments
 */
export function useEnvironments() {
  const { config, getAccessToken, isAuthenticated } = useAuth();
  const {
    environments: storeEnvironments,
    setEnvironments,
    addEnvironment: storeAddEnvironment,
    updateEnvironment: storeUpdateEnvironment,
    deleteEnvironment: storeDeleteEnvironment,
    activeWorkspaceId,
    activeEnvironmentId,
    setActiveEnvironment,
  } = useStore();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedWorkspaceRef = useRef<string | null>(null);

  const environments = Array.isArray(storeEnvironments) ? storeEnvironments : [];

  const api = useMemo(
    () => createEnvironmentApi(config, getAccessToken),
    [config, getAccessToken]
  );

  // Fetch all environments for the active workspace, including their variables
  const fetchEnvironments = useCallback(async () => {
    if (!isAuthenticated || !activeWorkspaceId) return;

    setIsLoading(true);
    setError(null);

    try {
      // 1. List environments
      const envList = await api.listEnvironments(activeWorkspaceId);

      // 2. Fetch details for each (to get variables)
      const fullEnvironments: Environment[] = await Promise.all(
        envList.map(async (e) => {
          try {
            const detail = await api.getEnvironment(e.id);
            return {
              id: detail.id,
              name: detail.name,
              isGlobal: detail.isGlobal,
              variables: (detail.variables || []).map((v) => ({
                key: v.key,
                value: v.value,
                isSecret: v.isSecret,
                enabled: v.enabled !== undefined ? v.enabled : true,
              })),
            };
          } catch (err) {
            console.error(`Failed to fetch environment ${e.id}:`, err);
            return {
              id: e.id,
              name: e.name,
              isGlobal: e.isGlobal,
              variables: [],
            };
          }
        })
      );

      setEnvironments(fullEnvironments);
      fetchedWorkspaceRef.current = activeWorkspaceId;
    } catch (err) {
      console.error('Failed to fetch environments:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch environments');
    } finally {
      setIsLoading(false);
    }
  }, [api, isAuthenticated, activeWorkspaceId, setEnvironments]);

  // Create an environment
  const createEnvironment = useCallback(
    async (data: { name: string; isGlobal?: boolean; variables?: Variable[] }): Promise<Environment | null> => {
      setError(null);

      if (!isAuthenticated || !activeWorkspaceId) {
        // Local-only fallback
        const newEnv: Environment = {
          id: generateId(),
          name: data.name || 'New Environment',
          isGlobal: data.isGlobal || false,
          variables: data.variables || [],
        };
        storeAddEnvironment(newEnv);
        return newEnv;
      }

      try {
        const created = await api.createEnvironment(activeWorkspaceId, {
          name: data.name,
          isGlobal: data.isGlobal,
          variables: data.variables?.map((v) => ({
            key: v.key,
            value: v.value,
            isSecret: v.isSecret,
            enabled: v.enabled,
          })),
        });

        const newEnv: Environment = {
          id: created.id,
          name: created.name,
          isGlobal: created.isGlobal,
          variables: (created.variables || []).map((v) => ({
            key: v.key,
            value: v.value,
            isSecret: v.isSecret,
            enabled: v.enabled !== undefined ? v.enabled : true,
          })),
        };
        storeAddEnvironment(newEnv);
        return newEnv;
      } catch (err) {
        console.error('Failed to create environment:', err);
        setError(err instanceof Error ? err.message : 'Failed to create environment');
        return null;
      }
    },
    [api, isAuthenticated, activeWorkspaceId, storeAddEnvironment]
  );

  // Update an environment
  const updateEnvironment = useCallback(
    async (id: string, updates: Partial<Omit<Environment, 'id'>>): Promise<boolean> => {
      setError(null);

      if (!isAuthenticated) {
        storeUpdateEnvironment(id, updates);
        return true;
      }

      try {
        const payload: { name?: string; isGlobal?: boolean; variables?: Array<{ key: string; value: string; isSecret?: boolean; enabled?: boolean }> } = {};
        if (updates.name !== undefined) payload.name = updates.name;
        if (updates.isGlobal !== undefined) payload.isGlobal = updates.isGlobal;
        if (updates.variables !== undefined) {
          payload.variables = updates.variables.map((v) => ({
            key: v.key,
            value: v.value,
            isSecret: v.isSecret,
            enabled: v.enabled,
          }));
        }

        const updated = await api.updateEnvironment(id, payload);
        storeUpdateEnvironment(id, {
          name: updated.name,
          isGlobal: updated.isGlobal,
          variables: (updated.variables || []).map((v) => ({
            key: v.key,
            value: v.value,
            isSecret: v.isSecret,
            enabled: v.enabled !== undefined ? v.enabled : true,
          })),
        });
        return true;
      } catch (err) {
        console.error('Failed to update environment:', err);
        setError(err instanceof Error ? err.message : 'Failed to update environment');
        return false;
      }
    },
    [api, isAuthenticated, storeUpdateEnvironment]
  );

  // Delete an environment
  const deleteEnvironment = useCallback(
    async (id: string): Promise<boolean> => {
      setError(null);

      if (!isAuthenticated) {
        storeDeleteEnvironment(id);
        return true;
      }

      try {
        await api.deleteEnvironment(id);
        storeDeleteEnvironment(id);
        return true;
      } catch (err) {
        console.error('Failed to delete environment:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete environment');
        return false;
      }
    },
    [api, isAuthenticated, storeDeleteEnvironment]
  );

  // Duplicate an environment (local + remote)
  const duplicateEnvironment = useCallback(
    async (env: Environment): Promise<Environment | null> => {
      return createEnvironment({
        name: `${env.name} (Copy)`,
        isGlobal: false,
        variables: env.variables.map((v) => ({ ...v })),
      });
    },
    [createEnvironment]
  );

  // Auto-fetch when workspace changes and user is authenticated
  useEffect(() => {
    if (isAuthenticated && activeWorkspaceId && fetchedWorkspaceRef.current !== activeWorkspaceId) {
      fetchEnvironments();
    }
  }, [isAuthenticated, activeWorkspaceId, fetchEnvironments]);

  return {
    environments,
    activeEnvironmentId,
    setActiveEnvironment,
    isLoading,
    error,
    fetchEnvironments,
    createEnvironment,
    updateEnvironment,
    deleteEnvironment,
    duplicateEnvironment,
  };
}
